import React, { ReactElement, useEffect, useReducer, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Key, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { Button, notification, Space, Tabs } from 'antd';
import { BreadcrumbItem } from 'types';
import { isEmpty } from 'lodash';

import { useLayoutState } from 'shared/components/layout/useLayoutState';
import { Layout } from 'shared/components/layout/Layout';
import {
  Contract,
  PricingRoutes,
  SortColumns,
  SearchContracts,
  ContractStatusId,
} from 'pricing/types';
import { ContractSearch } from 'pricing/components/contractSearch/contractSearch';
import ContractListFilters from 'pricing/components/contractList/contractListFilters/ContractListFilters';
import { getContracts } from 'pricing/pages/contractList/utilities/getContracts';
import { ContentStyled } from 'pricing/pages/contractList/ContractList.styles';
import ContractListTable from 'pricing/components/contractList/contractListTable/ContractListTable';
import { PricingLayoutPageHeaderStyled } from 'pricing/components/layout/PricingLayout.styles';
import { testId } from 'pricing/utilities/test/dataTestId';
import { ContractListInitState, contractsListReducer } from './ContractList.state';

const breadcrumbs: BreadcrumbItem[] = [
  {
    key: 'main',
    label: 'Main',
    path: '/',
  },
  {
    key: 'pricing',
    label: 'Pricing',
  },
  {
    key: 'contractList',
    label: 'Contract List',
  },
];

const breadcrumbsSearch: BreadcrumbItem[] = [
  ...breadcrumbs,
  {
    key: 'searchResults',
    label: 'Search results',
  },
];

export interface ContractListRouteParams {
  category?: SearchContracts;
  query?: string;
}

function Contracts(): ReactElement {
  const { TabPane } = Tabs;
  const history = useHistory();
  const [state, dispatch] = useReducer(contractsListReducer, ContractListInitState);
  const { search, filters, sort, pagination } = state;
  const { dispatch: dispatchLayout } = useLayoutState();
  const { category, query }: ContractListRouteParams = useParams();
  const [fetchContracts, setFetchContracts] = useState(false);

  const handleFetchContracts = async () => {
    dispatch({ type: 'LOAD_CONTRACTS' });
    try {
      const respGetContracts = await getContracts({ search, filters, sort, pagination });
      dispatch({
        type: 'SET_CONTRACTS',
        payload: {
          pagination: respGetContracts.pagination,
          contracts: respGetContracts.contracts,
        },
      });

      if (!isEmpty(filters)) {
        dispatch({ type: 'EXPAND_ALL_CONTRACTS', payload: true });
      }
    } catch (e) {
      e.message && notification.error({ message: e.message });
      dispatch({
        type: 'SET_CONTRACTS',
        payload: {
          pagination: ContractListInitState.pagination,
          contracts: [],
        },
      });
    }
  };

  const onPageChange = (page: number, pageSize: number | undefined) => {
    dispatch({
      type: 'PAGE_CONTRACTS',
      payload: {
        currentPage: page,
        totalContracts: state.pagination.totalContracts,
        pageSize,
      },
    });
  };

  const onChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<Contract> | SorterResult<Contract>[],
  ) => {
    if (!isEmpty(sorter)) {
      dispatch({
        type: 'SORT_CONTRACTS',
        payload: {
          columnKey: (sorter as SortColumns).columnKey,
          order: (sorter as SortColumns).order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
    }
  };

  const handleCreateContract = () => history.push(PricingRoutes.ContractCreate);

  const handleOnChangeTabs = (activeKey: string) => {
    const payload = { ...search };
    payload.breeder_contract_status_id = Number(activeKey);

    dispatch({
      type: 'SEARCH_CONTRACTS',
      payload,
    });
  };

  useEffect(() => {
    if (query && category) {
      dispatchLayout({ type: 'SET_BREADCRUMBS', breadcrumbs: breadcrumbsSearch });
      dispatch({
        type: 'SEARCH_CONTRACTS',
        payload: {
          breeder_contract_status_id: search.breeder_contract_status_id,
          [category]: query,
        },
      });
    } else {
      dispatchLayout({ type: 'SET_BREADCRUMBS', breadcrumbs });
      dispatch({
        type: 'SEARCH_CONTRACTS',
        payload: { breeder_contract_status_id: search.breeder_contract_status_id },
      });
    }

    setFetchContracts(true);
  }, [query, category]);

  useEffect(() => {
    fetchContracts && handleFetchContracts();
  }, [
    search,
    filters,
    sort,
    fetchContracts,
    pagination.pageSize,
    pagination.currentPage,
  ]);

  return (
    <Layout navigationCenterTools={<ContractSearch />}>
      <main data-testid="pricing-page-contract-list">
        <section data-testid={testId.pricingLayoutPageHeader}>
          <PricingLayoutPageHeaderStyled
            title="Contract List"
            extra={
              <Space>
                <Button type="primary" onClick={handleCreateContract}>
                  Upload New Contract
                </Button>
              </Space>
            }
          />
        </section>
        <ContentStyled>
          <Tabs
            defaultActiveKey={
              search?.breeder_contract_status_id?.toString() ||
              ContractStatusId.Active.toString()
            }
            onChange={handleOnChangeTabs}
          >
            <ContractListFilters
              dispatch={dispatch}
              resultsCounts={state.pagination.totalContracts}
            />
            <TabPane tab="Active" key={`${ContractStatusId.Active}`}>
              <ContractListTable
                dispatch={dispatch}
                contractsState={state}
                onChange={onChange}
                onPageChange={onPageChange}
              />
            </TabPane>
            <TabPane tab="Discharged" key={`${ContractStatusId.Discharged}`}>
              <ContractListTable
                dispatch={dispatch}
                contractsState={state}
                onChange={onChange}
                onPageChange={onPageChange}
              />
            </TabPane>
          </Tabs>
        </ContentStyled>
      </main>
    </Layout>
  );
}

export default Contracts;
